(function (global) {
  'use strict';

  const defaults = {
    title: 'Product details', close: 'Back to package', loading: 'Loading product details…',
    error: 'The extra product details could not load. You can still continue building your package.',
    retry: 'Try again', noDescription: 'More details are not listed for this item yet. Please contact us if you have a question.',
    noImage: 'No photo available', photos: 'Product photos', photo: 'Photo',
    zoom: 'Enlarge photo', unzoom: 'Fit photo', productPage: 'Full product page (opens a new tab)',
    selected: 'Previewing', galleryNote: 'Other photos may show additional colors or views.',
    unchanged: 'Your package selections stay in place.',
  };

  function webUrl(value, sameOrigin = false) {
    if (!value) return '';
    try {
      const url = new URL(value, global.location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      if (sameOrigin && url.origin !== global.location.origin) return '';
      return url.href;
    } catch (_) { return ''; }
  }

  function imageUrl(value) {
    return webUrl(typeof value === 'string' ? value : value?.src || value?.url);
  }

  function imageKey(value) {
    const url = new URL(value);
    url.searchParams.delete('width');
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.href;
  }

  function largeImage(value) {
    const url = new URL(value);
    if (url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/')) {
      url.searchParams.set('width', '1600');
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
    }
    return url.href;
  }

  function descriptionFragment(html) {
    const source = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const fragment = document.createDocumentFragment();
    const allowed = new Set(['P', 'BR', 'UL', 'OL', 'LI', 'STRONG', 'B', 'EM', 'I', 'H2', 'H3', 'H4', 'BLOCKQUOTE']);
    const omitted = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'SVG', 'TEMPLATE', 'NOSCRIPT']);
    function copy(node, parent) {
      if (node.nodeType === 3) { parent.append(document.createTextNode(node.textContent)); return; }
      if (node.nodeType !== 1 || omitted.has(node.tagName)) return;
      const target = allowed.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : parent;
      if (target !== parent) parent.append(target);
      for (const child of node.childNodes) copy(child, target);
    }
    for (const child of source.body.childNodes) copy(child, fragment);
    return fragment;
  }

  class PMSProductDetails {
    constructor(host, labels = {}) {
      this.labels = { ...defaults, ...labels };
      this.cache = new Map();
      this.sequence = 0;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'pms-product-details';
      this.dialog.setAttribute('aria-label', this.labels.title);
      this.dialog.innerHTML = '<div class="pms-product-details__shell"><header><div><h2></h2><p></p></div><button type="button" data-details-close autofocus></button></header><div class="pms-product-details__body"><section class="pms-product-details__gallery"><div class="pms-product-details__photo-box"><img alt=""><p data-details-no-image hidden></p></div><button type="button" data-details-zoom></button><div class="pms-product-details__thumbs"></div><small data-details-gallery-note></small></section><section class="pms-product-details__info"><p data-details-selection></p><p data-details-status role="status"></p><button type="button" data-details-retry hidden></button><div class="pms-product-details__description"></div><a data-details-link target="_blank" rel="noopener noreferrer"></a></section></div></div>';
      host.append(this.dialog);
      this.dialog.querySelector('header p').textContent = this.labels.unchanged;
      this.dialog.querySelector('[data-details-close]').textContent = this.labels.close;
      this.dialog.querySelector('[data-details-no-image]').textContent = this.labels.noImage;
      this.dialog.querySelector('[data-details-retry]').textContent = this.labels.retry;
      this.dialog.querySelector('[data-details-link]').textContent = this.labels.productPage;
      this.dialog.querySelector('[data-details-gallery-note]').textContent = this.labels.galleryNote;
      this.dialog.querySelector('.pms-product-details__thumbs').setAttribute('aria-label', this.labels.photos);
      this.dialog.querySelector('[data-details-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.querySelector('[data-details-retry]').addEventListener('click', () => this.load());
      this.dialog.querySelector('[data-details-zoom]').addEventListener('click', () => this.setZoom(!this.zoomed));
      this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.sequence += 1;
        if (this.scrollLocked) document.body.style.overflow = this.previousOverflow;
        this.scrollLocked = false;
        this.opener?.focus({ preventScroll: true });
      });
      this.dialog.querySelector('.pms-product-details__photo-box img').addEventListener('error', () => {
        this.dialog.querySelector('.pms-product-details__photo-box').setAttribute('aria-busy', 'false');
        this.dialog.querySelector('.pms-product-details__photo-box img').hidden = true;
        this.dialog.querySelector('[data-details-no-image]').hidden = false;
        this.dialog.querySelector('[data-details-zoom]').hidden = true;
      });
      this.dialog.querySelector('.pms-product-details__photo-box img').addEventListener('load', () => {
        this.dialog.querySelector('.pms-product-details__photo-box').setAttribute('aria-busy', 'false');
        this.dialog.querySelector('.pms-product-details__photo-box img').hidden = false;
      });
    }

    open(product, variant, opener) {
      this.product = product;
      this.variant = variant;
      this.opener = opener;
      this.dialog.querySelector('h2').textContent = product.title;
      this.dialog.setAttribute('aria-label', `${this.labels.title}: ${product.title}`);
      const selection = this.dialog.querySelector('[data-details-selection]');
      selection.textContent = variant?.title && variant.title !== 'Default Title' ? `${this.labels.selected}: ${variant.title}` : '';
      const link = this.dialog.querySelector('[data-details-link]');
      const productUrl = webUrl(product.url, true);
      link.hidden = !productUrl;
      if (productUrl) {
        const url = new URL(productUrl);
        if (variant?.id) url.searchParams.set('variant', variant.id);
        link.href = url.href;
      } else link.removeAttribute('href');
      this.dialog.querySelector('.pms-product-details__description').replaceChildren();
      this.renderImages(null);
      if (!this.dialog.open) {
        if (!this.scrollLocked) {
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.scrollLocked = true;
        }
        this.dialog.showModal();
      }
      this.dialog.querySelector('.pms-product-details__body').scrollTop = 0;
      this.load();
    }

    async load() {
      const sequence = ++this.sequence;
      const product = this.product;
      const handle = product.handle;
      const status = this.dialog.querySelector('[data-details-status]');
      const retry = this.dialog.querySelector('[data-details-retry]');
      retry.hidden = true;
      status.textContent = this.labels.loading;
      try {
        if (!handle) throw new Error('Missing product handle');
        let data = this.cache.get(handle);
        if (!data) {
          const root = global.Shopify?.routes?.root || '/';
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 10000);
          try {
            const response = await fetch(`${root.endsWith('/') ? root : `${root}/`}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' }, signal: controller.signal });
            if (!response.ok) throw new Error('Product unavailable');
            data = await response.json();
            if (!data || String(data.id) !== String(product.id)) throw new Error('Unexpected product');
            this.cache.set(handle, data);
          } finally { clearTimeout(timer); }
        }
        if (sequence !== this.sequence || !this.dialog.open) return;
        this.renderImages(data);
        const content = descriptionFragment(data.description);
        const description = this.dialog.querySelector('.pms-product-details__description');
        if (content.textContent.trim()) description.replaceChildren(content);
        else description.textContent = this.labels.noDescription;
        status.textContent = '';
      } catch (_) {
        if (sequence !== this.sequence || !this.dialog.open) return;
        status.textContent = this.labels.error;
        retry.hidden = !handle;
      }
    }

    renderImages(data) {
      const currentVariant = data?.variants?.find((variant) => String(variant.id) === String(this.variant?.id));
      const preferred = imageUrl(currentVariant?.featured_image) || imageUrl(this.variant?.image) || imageUrl(data?.featured_image) || imageUrl(this.product.featuredImage);
      const seen = new Set();
      this.images = [preferred, ...(data?.images || []).map(imageUrl)].filter((url) => {
        if (!url || seen.has(imageKey(url))) return false;
        seen.add(imageKey(url));
        return true;
      });
      const thumbnails = this.dialog.querySelector('.pms-product-details__thumbs');
      thumbnails.replaceChildren();
      this.images.forEach((url, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `${this.labels.photo} ${index + 1}`);
        const image = document.createElement('img');
        const thumbnail = new URL(url);
        if (thumbnail.hostname === 'cdn.shopify.com' || thumbnail.pathname.includes('/cdn/shop/')) thumbnail.searchParams.set('width', '140');
        image.src = thumbnail.href;
        image.alt = '';
        image.loading = 'lazy';
        button.append(image);
        button.addEventListener('click', () => this.selectImage(index));
        thumbnails.append(button);
      });
      thumbnails.hidden = this.images.length < 2;
      this.dialog.querySelector('[data-details-gallery-note]').hidden = this.images.length < 2;
      this.selectImage(0);
    }

    selectImage(index) {
      const image = this.dialog.querySelector('.pms-product-details__photo-box img');
      image.hidden = true;
      this.dialog.querySelector('.pms-product-details__photo-box').setAttribute('aria-busy', String(!!this.images.length));
      this.dialog.querySelector('[data-details-no-image]').hidden = !!this.images.length;
      this.dialog.querySelector('[data-details-zoom]').hidden = !this.images.length;
      if (this.images.length) {
        image.alt = `${this.product.title} — ${this.labels.photo} ${index + 1}`;
        image.src = largeImage(this.images[index]);
        if (image.complete && image.naturalWidth > 0) {
          image.hidden = false;
          this.dialog.querySelector('.pms-product-details__photo-box').setAttribute('aria-busy', 'false');
        }
      } else image.removeAttribute('src');
      Array.from(this.dialog.querySelector('.pms-product-details__thumbs').children).forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      this.setZoom(false);
    }

    setZoom(zoomed) {
      this.zoomed = zoomed;
      const box = this.dialog.querySelector('.pms-product-details__photo-box');
      box.classList.toggle('is-zoomed', zoomed);
      box.scrollTop = 0;
      box.scrollLeft = 0;
      const button = this.dialog.querySelector('[data-details-zoom]');
      button.textContent = zoomed ? this.labels.unzoom : this.labels.zoom;
      button.setAttribute('aria-pressed', String(zoomed));
    }

    destroy() {
      if (this.dialog.open) this.dialog.close();
      this.sequence += 1;
      this.dialog.remove();
    }
  }

  global.PMSProductDetails = PMSProductDetails;
})(window);
